import clsx from 'clsx';
import { Loader2, X } from 'lucide-react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

// --- Button --------------------------------------------------------------------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'plain';
const variants: Record<Variant, string> = {
  primary: 'bg-lime text-lime-ink hover:brightness-[1.04] active:brightness-95 shadow-[0_1px_0_rgba(255,255,255,.5)_inset,0_6px_16px_-8px_rgba(120,150,0,.6)]',
  secondary: 'bg-surface-solid text-ink ring-1 ring-hairline-strong hover:bg-surface-2 active:bg-hairline',
  ghost: 'text-ink hover:bg-ink/[0.06] active:bg-ink/10',
  danger: 'bg-bad text-white hover:brightness-110 active:brightness-95',
  plain: 'text-link hover:underline underline-offset-2 px-0!',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md';
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-full font-semibold whitespace-nowrap transition-[filter,background-color,box-shadow] duration-150 disabled:opacity-45 disabled:pointer-events-none select-none',
        size === 'sm' ? 'h-8 px-3.5 text-caption' : 'h-10 px-4.5 text-body',
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-3.5 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

// --- Layout pieces -------------------------------------------------------------

export function PageHeader({ title, subtitle, actions, children, back }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children?: ReactNode; back?: () => void }) {
  return (
    <header className="mb-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          {back && (
            <button onClick={back} aria-label="Retour" className="mt-1.5 flex size-10 shrink-0 items-center justify-center rounded-full bg-surface-solid ring-1 ring-hairline hover:bg-surface-2">
              <svg viewBox="0 0 16 16" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden><path d="M10 3 5 8l5 5" /></svg>
            </button>
          )}
          <div className="min-w-0">
            <h1 className="text-large-title font-medium">{title}</h1>
            {subtitle && <p className="mt-1.5 max-w-[72ch] text-muted">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

/** Apple-style inset grouped list. Title sits outside, above the group. */
export function Group({ title, description, actions, children, className }: { title?: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={clsx('glass mb-6 overflow-hidden rounded-[26px]', className)}>
      {(title || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3 px-6 pt-5 pb-3">
          <div className="min-w-0">
            {title && <h2 className="text-title font-semibold">{title}</h2>}
            {description && <p className="mt-1 max-w-[76ch] text-caption text-muted">{description}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={clsx((title || actions) && 'border-t border-hairline')}>{children}</div>
    </section>
  );
}

export function Row({ label, children, hint, className }: { label: ReactNode; children: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <div className={clsx('flex min-h-13 flex-wrap items-center justify-between gap-x-6 gap-y-1 border-b border-hairline px-6 py-3 last:border-b-0', className)}>
      <div className="min-w-0">
        <div>{label}</div>
        {hint && <div className="text-caption text-muted">{hint}</div>}
      </div>
      <div className="min-w-0 text-right text-muted">{children}</div>
    </div>
  );
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={clsx('font-mono text-[0.8em] tracking-tight', className)}>{children}</span>;
}

export function Spinner({ label = 'Chargement' }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-2 py-10 text-muted justify-center">
      <Loader2 className="size-4 animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  );
}

export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      {icon && <div className="mb-3 text-faint [&>svg]:size-8">{icon}</div>}
      <p className="font-semibold">{title}</p>
      {children && <p className="mt-1 max-w-[52ch] text-muted">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

const bannerTone = {
  info: 'bg-surface-solid/70 text-ink ring-hairline-strong',
  warning: 'bg-warn-soft text-ink ring-warn/30',
  critical: 'bg-bad-soft text-ink ring-bad/30',
  success: 'bg-ok-soft text-ink ring-ok/30',
};

export function Banner({ tone = 'info', icon, title, children, action }: { tone?: keyof typeof bannerTone; icon?: ReactNode; title?: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div role={tone === 'critical' ? 'alert' : 'note'} className={clsx('mb-5 flex gap-3 rounded-[20px] px-5 py-4 ring-1 backdrop-blur', bannerTone[tone])}>
      {icon && <div className="mt-0.5 shrink-0 [&>svg]:size-4">{icon}</div>}
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="text-[0.8125rem] text-ink/80">{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}

const pillTone = {
  neutral: 'bg-unknown-soft text-muted',
  accent: 'bg-lime-soft text-ink',
  lime: 'bg-lime text-lime-ink',
  dark: 'bg-panel text-panel-ink',
  ok: 'bg-ok-soft text-ok',
  warn: 'bg-warn-soft text-warn',
  bad: 'bg-bad-soft text-bad',
  prod: 'bg-prod-soft text-prod',
};
export type PillTone = keyof typeof pillTone;

export function Pill({ tone = 'neutral', children, icon, className, title }: { tone?: PillTone; children: ReactNode; icon?: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={clsx('inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-caption font-semibold whitespace-nowrap [&>svg]:size-3', pillTone[tone], className)}>
      {icon}
      {children}
    </span>
  );
}

// --- Form controls -------------------------------------------------------------

const control =
  'rounded-full border border-hairline-strong bg-surface-solid px-4 text-body text-ink placeholder:text-faint transition-shadow focus:border-ink/40 focus:outline-none focus:ring-4 focus:ring-[var(--focus)] disabled:opacity-60';

export function Field({ label, hint, error, children, htmlFor }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="mb-4">
      <label htmlFor={htmlFor} className="mb-1 block text-[0.8125rem] font-medium">
        {label}
      </label>
      {children}
      {error ? <p className="mt-1 text-caption text-bad">{error}</p> : hint ? <p className="mt-1 text-caption text-muted">{hint}</p> : null}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...p }, ref) {
  return <input ref={ref} className={clsx(control, 'h-10', !/\bw-/.test(className ?? '') && 'w-full', className)} {...p} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...p }, ref) {
  return <textarea ref={ref} className={clsx(control, 'min-h-24 w-full rounded-[18px]! py-2.5', className)} {...p} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...p }, ref) {
  return (
    <select ref={ref} className={clsx(control, !/\bw-/.test(className ?? '') && 'w-full', 'h-10 appearance-none bg-[length:12px] bg-[right_14px_center] bg-no-repeat pr-9', className)} style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none' stroke='%236a6f78' stroke-width='1.5'%3E%3Cpath d='M3 4.5 6 7.5 9 4.5'/%3E%3C/svg%3E\")" }} {...p}>
      {children}
    </select>
  );
});

export function Checkbox({ label, hint, ...p }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className="mb-3 flex gap-2.5">
      <input id={id} type="checkbox" className="mt-0.5 size-4 shrink-0 accent-[var(--ink)]" {...p} />
      <label htmlFor={id} className="cursor-pointer">
        <span className="block">{label}</span>
        {hint && <span className="block text-caption text-muted">{hint}</span>}
      </label>
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={clsx('relative h-[24px] w-[42px] shrink-0 rounded-full transition-colors disabled:opacity-50', checked ? 'bg-lime' : 'bg-hairline-strong')}
    >
      <span className={clsx('absolute top-[3px] left-0 size-[18px] rounded-full shadow transition-transform', checked ? 'translate-x-[21px] bg-lime-ink' : 'translate-x-[3px] bg-white')} />
    </button>
  );
}

// --- Segmented control (tabs) --------------------------------------------------

export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; badge?: ReactNode }[]; label: string }) {
  return (
    <div role="tablist" aria-label={label} className="inline-flex max-w-full gap-1.5 overflow-x-auto rounded-full p-1">
      {options.map((o) => (
        <button
          key={o.value}
          role="tab"
          type="button"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
          className={clsx(
            'flex h-9 items-center gap-1.5 rounded-full px-4 text-[0.8125rem] font-semibold whitespace-nowrap transition-colors',
            value === o.value ? 'bg-lime text-lime-ink shadow-[0_6px_16px_-10px_rgba(120,150,0,.7)]' : 'bg-surface-solid text-ink/70 ring-1 ring-hairline hover:text-ink',
          )}
        >
          {o.label}
          {o.badge}
        </button>
      ))}
    </div>
  );
}

// --- Sheet (modal dialog) -------------------------------------------------------

export function Sheet({ open, onClose, title, children, footer, width = 'md' }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; width?: 'md' | 'lg' }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === ref.current && onClose()}
      className={clsx(
        'm-auto max-h-[88vh] w-[calc(100%-2rem)] rounded-[28px] bg-surface-solid p-0 text-ink shadow-pop backdrop:bg-[rgba(18,20,23,.35)] backdrop:backdrop-blur-[4px]',
        width === 'lg' ? 'max-w-3xl' : 'max-w-lg',
      )}
    >
      {open && (
        <div className="flex max-h-[88vh] flex-col">
          <div className="flex items-center justify-between border-b border-hairline px-6 py-4">
            <h2 className="text-title font-semibold">{title}</h2>
            <button onClick={onClose} aria-label="Fermer" className="flex size-9 items-center justify-center rounded-full text-muted ring-1 ring-hairline hover:bg-ink/5">
              <X className="size-4" />
            </button>
          </div>
          <div className="overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="flex justify-end gap-2 border-t border-hairline px-6 py-4">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

// --- Toasts -------------------------------------------------------------------

interface Toast {
  id: number;
  tone: 'success' | 'error' | 'info';
  text: string;
}
const ToastCtx = createContext<(tone: Toast['tone'], text: string) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((tone: Toast['tone'], text: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, tone, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 7000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div aria-live="polite" className="pointer-events-none fixed right-4 bottom-4 z-50 flex w-[min(380px,calc(100%-2rem))] flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={clsx(
              'pointer-events-auto flex items-center gap-2.5 rounded-full bg-panel px-5 py-3 text-[0.8125rem] font-medium text-panel-ink shadow-pop animate-[toast_.22s_ease-out]',
            )}
          >
            <span aria-hidden className={clsx('size-2 shrink-0 rounded-full', t.tone === 'error' ? 'bg-bad' : t.tone === 'success' ? 'bg-lime' : 'bg-panel-muted')} />
            {t.text}
          </div>
        ))}
      </div>
      <style>{'@keyframes toast{from{opacity:0;transform:translateY(8px) scale(.98)}to{opacity:1;transform:none}}'}</style>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

// --- Metrics (large light numerals) ------------------------------------------------

export function Metric({ label, value, unit, hint, tone }: { label: ReactNode; value: ReactNode; unit?: ReactNode; hint?: ReactNode; tone?: 'bad' | 'warn' | 'ok' }) {
  return (
    <div className="min-w-0">
      <p className="text-caption font-medium text-muted">{label}</p>
      <p className={clsx('num mt-1 flex items-baseline gap-1.5 text-metric font-light', tone === 'bad' && 'text-bad', tone === 'warn' && 'text-warn', tone === 'ok' && 'text-ok')}>
        {value}
        {unit && <span className="text-body font-normal text-muted">{unit}</span>}
      </p>
      {hint && <p className="mt-1 text-caption text-muted">{hint}</p>}
    </div>
  );
}

/** Hatched progress bar: lime for the measured share, muted hatching for the rest. */
export function Stripes({ value, label }: { value: number; label: string }) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} className="stripes-muted flex h-2.5 overflow-hidden rounded-full">
      <div className="stripes h-full rounded-full" style={{ width: `${pct}%` }} />
    </div>
  );
}
